// Valutatore del DSL condizioni dei flow — port TypeScript di
// `app/Services/Triggers/TriggerConditionEvaluator.php` (api-dev).
//
// Stessa forma JSON (AND/OR annidati, `field`/`operator`/`value`) e stesso set
// di operatori, così l'admin usa un'unica sintassi per trigger e wizard.
//
// DIVERGENZE VOLUTE rispetto al PHP, documentate perché chi porterà modifiche
// da un lato all'altro deve conoscerle:
//   1. `not` — il PHP non lo ha. Aggiunto perché al builder serve la negazione.
//   2. `==` NON eredita le conversioni pericolose di PHP: `null == 0` e
//      `null == false` sono FALSE qui (in PHP sono true). `null == ''` resta true.
//   3. Operatore ignoto → a runtime `false` + warn, NON un'eccezione: un flow
//      malformato non deve piantare l'operatore a metà wizard. Il rifiuto
//      avviene in fase di pubblicazione, tramite `collectConditionIssues()`.
//   4. `empty` considera vuoto anche `{}` (per le risposte dei nodi form).

import type {
  FlowAndCondition,
  FlowCondition,
  FlowFieldRef,
  FlowLeafCondition,
  FlowNotCondition,
  FlowOperator,
  FlowOrCondition,
  FlowScope,
} from './flowTypes'
import { getScopeValue } from './scope'
import { resolveTemplate } from './template'

export interface FlowConditionTraceEntry {
  operator: string
  field?: string
  fieldValue?: unknown
  expected?: unknown
  result: boolean
}

const MAX_REGEX_LENGTH = 500

const KNOWN_OPERATORS: ReadonlySet<string> = new Set<FlowOperator>([
  '==', 'eq', 'equals',
  '!=', 'ne', 'not_equals',
  '>', 'gt', '>=', 'gte', '<', 'lt', '<=', 'lte',
  'in', 'not_in',
  'contains',
  'is_null', 'null', 'not_null', 'not_nullable',
  'changed',
  'matches', 'regex',
  'before', 'after', 'between',
  'starts_with', 'ends_with',
  'empty', 'not_empty',
])

// ─── Discriminanti di forma ───────────────────────────────────────────────────

function isLeaf(c: FlowCondition): c is FlowLeafCondition {
  return !Array.isArray(c) && typeof c === 'object' && c !== null
    && 'field' in c && 'operator' in c
}
function isAnd(c: FlowCondition): c is FlowAndCondition {
  return !Array.isArray(c) && typeof c === 'object' && c !== null
    && Array.isArray((c as FlowAndCondition).and)
}
function isOr(c: FlowCondition): c is FlowOrCondition {
  return !Array.isArray(c) && typeof c === 'object' && c !== null
    && Array.isArray((c as FlowOrCondition).or)
}
function isNot(c: FlowCondition): c is FlowNotCondition {
  return !Array.isArray(c) && typeof c === 'object' && c !== null
    && (c as FlowNotCondition).not !== undefined
}
function isFieldRef(v: unknown): v is FlowFieldRef {
  return typeof v === 'object' && v !== null && typeof (v as FlowFieldRef).$field === 'string'
}

// ─── API pubblica ─────────────────────────────────────────────────────────────

/**
 * Valuta un albero di condizioni. Una condizione assente/vuota vale `true`
 * (come nel PHP: "nessun filtro" ⇒ passa).
 */
export function evaluateCondition(
  condition: FlowCondition | null | undefined,
  scope: FlowScope,
  trace?: FlowConditionTraceEntry[],
): boolean {
  if (condition === null || condition === undefined) return true

  if (Array.isArray(condition)) return evaluateAll(condition, scope, trace)
  if (isLeaf(condition)) return evaluateLeaf(condition, scope, trace)
  if (isAnd(condition)) return evaluateAll(condition.and, scope, trace)
  if (isOr(condition)) return evaluateAny(condition.or, scope, trace)
  if (isNot(condition)) return !evaluateCondition(condition.not, scope, trace)

  // Oggetto senza forma riconoscibile: non filtra nulla.
  return true
}

/**
 * Controlli di forma da eseguire in fase di PUBBLICAZIONE del flow (non a
 * runtime): segnala operatori inesistenti, `field` mancanti, regex illegali.
 * Restituisce la lista dei problemi; vuota = condizione pubblicabile.
 */
export function collectConditionIssues(
  condition: FlowCondition | null | undefined,
  path = 'condition',
): string[] {
  if (condition === null || condition === undefined) return []
  const issues: string[] = []

  if (Array.isArray(condition)) {
    condition.forEach((c, i) => issues.push(...collectConditionIssues(c, `${path}[${i}]`)))
    return issues
  }
  if (isAnd(condition)) {
    condition.and.forEach((c, i) => issues.push(...collectConditionIssues(c, `${path}.and[${i}]`)))
    return issues
  }
  if (isOr(condition)) {
    condition.or.forEach((c, i) => issues.push(...collectConditionIssues(c, `${path}.or[${i}]`)))
    return issues
  }
  if (isNot(condition)) {
    return collectConditionIssues(condition.not, `${path}.not`)
  }
  if (isLeaf(condition)) {
    if (!condition.field) issues.push(`${path}: 'field' mancante`)
    if (!KNOWN_OPERATORS.has(condition.operator)) {
      issues.push(`${path}: operatore non supportato '${condition.operator}'`)
    }
    if ((condition.operator === 'matches' || condition.operator === 'regex')) {
      const pattern = condition.value
      if (typeof pattern !== 'string') issues.push(`${path}: 'matches' richiede un pattern stringa`)
      else if (pattern.length > MAX_REGEX_LENGTH) issues.push(`${path}: pattern regex troppo lungo`)
      else if (compileRegex(pattern) === null) issues.push(`${path}: pattern regex non valido`)
    }
    if (condition.operator === 'between' && !Array.isArray(condition.value)) {
      issues.push(`${path}: 'between' richiede un array [min, max]`)
    }
    return issues
  }

  issues.push(`${path}: forma non riconosciuta (attesi 'field'/'operator', 'and', 'or', 'not')`)
  return issues
}

// ─── Gruppi ───────────────────────────────────────────────────────────────────

function evaluateAll(list: FlowCondition[], scope: FlowScope, trace?: FlowConditionTraceEntry[]): boolean {
  for (const c of list) {
    if (!evaluateCondition(c, scope, trace)) return false
  }
  return true
}

function evaluateAny(list: FlowCondition[], scope: FlowScope, trace?: FlowConditionTraceEntry[]): boolean {
  for (const c of list) {
    if (evaluateCondition(c, scope, trace)) return true
  }
  return false
}

// ─── Foglia ───────────────────────────────────────────────────────────────────

function evaluateLeaf(
  condition: FlowLeafCondition,
  scope: FlowScope,
  trace?: FlowConditionTraceEntry[],
): boolean {
  const fieldValue = getScopeValue(scope, condition.field)
  const expected = resolveExpected(condition.value, scope)
  const caseSensitive = condition.options?.case_sensitive === true

  let result: boolean
  switch (condition.operator) {
    case '==': case 'eq': case 'equals':
      result = looseEquals(fieldValue, expected); break
    case '!=': case 'ne': case 'not_equals':
      result = !looseEquals(fieldValue, expected); break
    case '>': case 'gt':
      result = compare(fieldValue, expected, (a, b) => a > b); break
    case '>=': case 'gte':
      result = compare(fieldValue, expected, (a, b) => a >= b); break
    case '<': case 'lt':
      result = compare(fieldValue, expected, (a, b) => a < b); break
    case '<=': case 'lte':
      result = compare(fieldValue, expected, (a, b) => a <= b); break
    case 'in':
      result = toArray(expected).some((v) => looseEquals(fieldValue, v)); break
    case 'not_in':
      result = !toArray(expected).some((v) => looseEquals(fieldValue, v)); break
    case 'contains':
      result = evaluateContains(fieldValue, expected, caseSensitive); break
    case 'is_null': case 'null':
      result = fieldValue === null || fieldValue === undefined; break
    case 'not_null': case 'not_nullable':
      result = fieldValue !== null && fieldValue !== undefined; break
    case 'changed':
      result = evaluateChanged(condition.field, expected, scope); break
    case 'matches': case 'regex':
      result = evaluateMatches(fieldValue, expected); break
    case 'before':
      result = compareDates(fieldValue, expected, (a, b) => a < b); break
    case 'after':
      result = compareDates(fieldValue, expected, (a, b) => a > b); break
    case 'between':
      result = evaluateBetween(fieldValue, expected); break
    case 'starts_with':
      result = typeof fieldValue === 'string' && typeof expected === 'string'
        && fieldValue.startsWith(expected); break
    case 'ends_with':
      result = typeof fieldValue === 'string' && typeof expected === 'string'
        && fieldValue.endsWith(expected); break
    case 'empty':
      result = isEmpty(fieldValue); break
    case 'not_empty':
      result = !isEmpty(fieldValue); break
    default:
      console.warn(`[flow/conditions] operatore non supportato: '${condition.operator}' su '${condition.field}'`)
      result = false
  }

  trace?.push({
    operator: condition.operator,
    field: condition.field,
    fieldValue,
    expected,
    result,
  })

  return result
}

/** Il valore atteso può essere un template, o un riferimento a un altro campo. */
function resolveExpected(value: unknown, scope: FlowScope): unknown {
  if (isFieldRef(value)) return getScopeValue(scope, value.$field)
  if (Array.isArray(value)) return value.map((v) => resolveExpected(v, scope))
  return resolveTemplate(value, scope)
}

// ─── Operatori ────────────────────────────────────────────────────────────────

function toArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value
  if (value === null || value === undefined) return []
  return [value]
}

function isNullish(v: unknown): boolean {
  return v === null || v === undefined
}

function isNumericLike(v: unknown): boolean {
  if (typeof v === 'number') return Number.isFinite(v)
  if (typeof v === 'string' && v.trim() !== '') return Number.isFinite(Number(v))
  return false
}

function toBool(v: unknown): boolean {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v !== 0
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase()
    if (s === 'true' || s === '1') return true
    if (s === 'false' || s === '0' || s === '') return false
  }
  return Boolean(v)
}

/**
 * Uguaglianza "type-aware": numeri confrontati come numeri, booleani come
 * booleani, il resto come stringhe. `null`/`undefined` sono uguali fra loro e
 * alla stringa vuota, ma NON a `0` né a `false`.
 */
function looseEquals(a: unknown, b: unknown): boolean {
  if (isNullish(a) && isNullish(b)) return true
  if (isNullish(a)) return b === ''
  if (isNullish(b)) return a === ''

  if (typeof a === 'boolean' || typeof b === 'boolean') return toBool(a) === toBool(b)
  if (isNumericLike(a) && isNumericLike(b)) return Number(a) === Number(b)

  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => looseEquals(v, b[i]))
  }
  if (typeof a === 'object' || typeof b === 'object') {
    try {
      return JSON.stringify(a) === JSON.stringify(b)
    } catch {
      return false
    }
  }

  return String(a) === String(b)
}

/** Confronto d'ordine: date se almeno un lato è una data, altrimenti numeri/stringhe. */
function compare(a: unknown, b: unknown, op: (x: number, y: number) => boolean): boolean {
  if (looksLikeDate(a) || looksLikeDate(b)) return compareDates(a, b, op)
  if (isNullish(a) || isNullish(b)) return false
  if (isNumericLike(a) && isNumericLike(b)) return op(Number(a), Number(b))
  const sa = String(a)
  const sb = String(b)
  return op(sa < sb ? -1 : sa > sb ? 1 : 0, 0)
}

function compareDates(a: unknown, b: unknown, op: (x: number, y: number) => boolean): boolean {
  const da = parseFlowDate(a)
  const db = parseFlowDate(b)
  if (!da || !db) return false
  return op(da.getTime(), db.getTime())
}

function evaluateContains(fieldValue: unknown, expected: unknown, caseSensitive: boolean): boolean {
  if (Array.isArray(fieldValue)) return fieldValue.some((v) => looseEquals(v, expected))
  if (typeof fieldValue === 'string' && typeof expected === 'string') {
    return caseSensitive
      ? fieldValue.includes(expected)
      : fieldValue.toLowerCase().includes(expected.toLowerCase())
  }
  return false
}

/**
 * `changed`: nel wizard non esiste il dirty-tracking di Eloquent, quindi si
 * appoggia a `scope.changes` / `scope.original` se l'host li fornisce.
 */
function evaluateChanged(field: string, expected: unknown, scope: FlowScope): boolean {
  const plain = field.replace(/^item\./, '')
  const changes = scope.changes
  const original = scope.original

  if (!changes && !original) {
    console.warn(`[flow/conditions] 'changed' su '${field}' ignorato: lo scope non espone original/changes`)
    return false
  }

  const current = getScopeValue(scope, field)
  let changed: boolean
  if (changes && Object.prototype.hasOwnProperty.call(changes, plain)) changed = true
  else if (original && Object.prototype.hasOwnProperty.call(original, plain)) {
    changed = !looseEquals(original[plain], current)
  } else changed = false

  if (!changed) return false
  if (isNullish(expected)) return true
  return looseEquals(current, expected)
}

function compileRegex(pattern: string): RegExp | null {
  try {
    // Supporta sia il pattern nudo (`^AB`) sia la forma con delimitatori PHP (`/^AB/i`).
    const delimited = /^\/(.*)\/([gimsuy]*)$/.exec(pattern)
    return delimited ? new RegExp(delimited[1], delimited[2]) : new RegExp(pattern)
  } catch {
    return null
  }
}

function evaluateMatches(fieldValue: unknown, pattern: unknown): boolean {
  if (typeof fieldValue !== 'string' || typeof pattern !== 'string') return false
  if (pattern.length > MAX_REGEX_LENGTH) {
    console.warn('[flow/conditions] pattern regex troppo lungo, condizione ignorata')
    return false
  }
  const re = compileRegex(pattern)
  if (!re) {
    console.warn('[flow/conditions] pattern regex non valido:', pattern)
    return false
  }
  return re.test(fieldValue)
}

function evaluateBetween(fieldValue: unknown, expected: unknown): boolean {
  if (!Array.isArray(expected) || expected.length !== 2) {
    console.warn("[flow/conditions] 'between' richiede un array [min, max]")
    return false
  }
  const [min, max] = expected
  if (looksLikeDate(fieldValue) || looksLikeDate(min)) {
    return compareDates(fieldValue, min, (a, b) => a >= b)
      && compareDates(fieldValue, max, (a, b) => a <= b)
  }
  return compare(fieldValue, min, (a, b) => a >= b) && compare(fieldValue, max, (a, b) => a <= b)
}

/** Mirror di `empty()` PHP, più `{}` (utile per le risposte dei nodi form). */
function isEmpty(v: unknown): boolean {
  if (isNullish(v)) return true
  if (typeof v === 'string') return v === '' || v === '0'
  if (typeof v === 'number') return v === 0
  if (typeof v === 'boolean') return v === false
  if (Array.isArray(v)) return v.length === 0
  if (v instanceof Date) return false
  if (typeof v === 'object') return Object.keys(v as Record<string, unknown>).length === 0
  return false
}

// ─── Date ─────────────────────────────────────────────────────────────────────

const DATE_KEYWORDS = new Set(['now', 'today', 'yesterday', 'tomorrow'])
const ISO_DATE = /^\d{4}-\d{2}-\d{2}/
const IT_DATE = /^(\d{2})[/-](\d{2})[/-](\d{4})$/

export function looksLikeDate(value: unknown): boolean {
  if (value instanceof Date) return true
  if (typeof value !== 'string') return false
  const s = value.trim()
  return DATE_KEYWORDS.has(s.toLowerCase()) || ISO_DATE.test(s) || IT_DATE.test(s)
}

/** Accetta `Date`, keyword (`today`, ...), ISO `YYYY-MM-DD[...]`, e `DD/MM/YYYY`. */
export function parseFlowDate(value: unknown): Date | null {
  if (value === null || value === undefined) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value !== 'string') return null

  const s = value.trim()
  const startOfToday = () => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }

  switch (s.toLowerCase()) {
    case 'now': return new Date()
    case 'today': return startOfToday()
    case 'yesterday': {
      const d = startOfToday()
      d.setDate(d.getDate() - 1)
      return d
    }
    case 'tomorrow': {
      const d = startOfToday()
      d.setDate(d.getDate() + 1)
      return d
    }
  }

  const it = IT_DATE.exec(s)
  if (it) {
    const d = new Date(Number(it[3]), Number(it[2]) - 1, Number(it[1]))
    return Number.isNaN(d.getTime()) ? null : d
  }

  if (ISO_DATE.test(s)) {
    // `YYYY-MM-DD` nudo va interpretato come data LOCALE: `new Date('2026-01-02')`
    // in JS è UTC e sfasa il confronto di un giorno nei fusi a est/ovest.
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
    if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
    const d = new Date(s.replace(' ', 'T'))
    return Number.isNaN(d.getTime()) ? null : d
  }

  return null
}
